import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Save,
  Star,
  Recommend,
  VisibilityOff,
  Visibility,
  Tune,
} from "@mui/icons-material";
import { getHttpClient } from "src/services/rest-api";
import { HTTPService } from "src/services/httpservice";

interface PlanFeature {
  feature_key: string;
  enabled: boolean;
}

interface PlanPricing {
  id?: number;
  country_code: string;
  currency: string;
  billing_cycle: "MONTHLY" | "YEARLY";
  price_per_store: number;
}

interface Plan {
  plan_id: number;
  name: string;
  description: string;
  sort_order: number;
  is_active: boolean;
  is_default: boolean;
  visibility: "VISIBLE" | "HIDDEN";
  popular: boolean;
  recommended: boolean;
  badge: string | null;
  theme_color: string | null;
  checkout_type?: string;
  version: number;
  features: PlanFeature[];
  pricing: PlanPricing[];
}

// Canonical product modules — keep in sync with backend PlanFeatures seed.
const FEATURE_LABELS: Record<string, string> = {
  workflow: "Workflow Management",
  food_safety: "Food Safety Management",
  incident: "Accident / Incident Management",
  employee: "Employee Management",
  training: "Employee Training",
  analytics: "Reports & Analytics",
  area_management: "Area-wise Management",
  admin_portal: "Centralized Admin Portal",
};
const FEATURE_KEYS = Object.keys(FEATURE_LABELS);
const featureLabel = (key: string) =>
  FEATURE_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const COUNTRY_OPTIONS = [
  { code: "GB", label: "United Kingdom", flag: "🇬🇧", currency: "GBP" },
  { code: "US", label: "United States", flag: "🇺🇸", currency: "USD" },
  { code: "AU", label: "Australia", flag: "🇦🇺", currency: "AUD" },
  { code: "IN", label: "India", flag: "🇮🇳", currency: "INR" },
];

const CURRENCY_SYMBOL: Record<string, string> = { GBP: "£", USD: "$", AUD: "A$", INR: "₹" };
const CYCLES: Array<"MONTHLY" | "YEARLY"> = ["MONTHLY", "YEARLY"];

const cellKey = (code: string, cycle: string) => `${code}:${cycle}`;

const emptyPlan: Partial<Plan> = {
  name: "",
  description: "",
  sort_order: 0,
  is_active: true,
  is_default: false,
  visibility: "VISIBLE",
  popular: false,
  recommended: false,
  badge: "",
  theme_color: "#6366F1",
};

const PlanCustomizer: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Plan CRUD dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editPlan, setEditPlan] = useState<Partial<Plan>>(emptyPlan);
  const [isNewPlan, setIsNewPlan] = useState(false);
  const [saving, setSaving] = useState(false);

  // Inline pricing matrix — planId -> { "GB:MONTHLY": "30", ... }
  const [priceMatrix, setPriceMatrix] = useState<Record<string, Record<string, string>>>({});
  const [savingPrices, setSavingPrices] = useState<string | null>(null);

  // Feature dialog
  const [featureDialog, setFeatureDialog] = useState(false);
  const [featurePlan, setFeaturePlan] = useState<Plan | null>(null);
  const [savingFeature, setSavingFeature] = useState(false);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getHttpClient(HTTPService.PLANS_APPADMIN, "GET", null, null, null);
      setPlans(res.data?.data || []);
    } catch (err: any) {
      setError(err?.data?.message || "Failed to load plans.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Rebuild the editable matrix whenever plans change
  useEffect(() => {
    const next: Record<string, Record<string, string>> = {};
    for (const plan of plans) {
      const cells: Record<string, string> = {};
      for (const c of COUNTRY_OPTIONS) {
        for (const cy of CYCLES) {
          const found = plan.pricing?.find((pr) => pr.country_code === c.code && pr.billing_cycle === cy);
          cells[cellKey(c.code, cy)] = found ? String(found.price_per_store) : "";
        }
      }
      next[String(plan.plan_id)] = cells;
    }
    setPriceMatrix(next);
  }, [plans]);

  const originalPrice = (plan: Plan, code: string, cycle: string) => {
    const f = plan.pricing?.find((pr) => pr.country_code === code && pr.billing_cycle === cycle);
    return f ? String(f.price_per_store) : "";
  };

  const setCell = (planId: number, code: string, cycle: string, value: string) => {
    setPriceMatrix((m) => ({
      ...m,
      [String(planId)]: { ...(m[String(planId)] || {}), [cellKey(code, cycle)]: value },
    }));
  };

  const isPlanDirty = (plan: Plan) => {
    const cells = priceMatrix[String(plan.plan_id)] || {};
    return COUNTRY_OPTIONS.some((c) =>
      CYCLES.some((cy) => (cells[cellKey(c.code, cy)] ?? "") !== originalPrice(plan, c.code, cy))
    );
  };

  // ── Plan Save ────────────────────────────────────────────────────────────
  const handleSavePlan = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isNewPlan) {
        await getHttpClient(HTTPService.PLANS_APPADMIN, "POST", editPlan, null, null);
        setSuccess("Plan created successfully.");
      } else {
        await getHttpClient(`${HTTPService.PLANS_APPADMIN}/${editPlan.plan_id}`, "PUT", editPlan, null, null);
        setSuccess("Plan updated successfully.");
      }
      setEditDialog(false);
      fetchPlans();
    } catch (err: any) {
      setError(err?.data?.message || "Failed to save plan.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async (planId: number) => {
    if (!window.confirm("Are you sure you want to soft-delete this plan?")) return;
    try {
      await getHttpClient(`${HTTPService.PLANS_APPADMIN}/${planId}`, "DELETE", null, null, null);
      setSuccess("Plan deleted.");
      fetchPlans();
    } catch (err: any) {
      setError(err?.data?.message || "Failed to delete plan.");
    }
  };

  // ── Pricing Save (batch all changed cells for one plan) ────────────────────
  const handleSavePrices = async (plan: Plan) => {
    const cells = priceMatrix[String(plan.plan_id)] || {};
    const requests: Promise<any>[] = [];
    for (const c of COUNTRY_OPTIONS) {
      for (const cy of CYCLES) {
        const val = (cells[cellKey(c.code, cy)] ?? "").trim();
        const orig = originalPrice(plan, c.code, cy);
        // Skip unchanged cells, and skip blanks (no delete endpoint — leave existing untouched)
        if (val === orig || val === "") continue;
        requests.push(
          getHttpClient(
            `${HTTPService.PLANS_APPADMIN}/${plan.plan_id}/pricing`,
            "PUT",
            { country_code: c.code, currency: c.currency, billing_cycle: cy, price_per_store: Number(val) },
            null,
            null
          )
        );
      }
    }
    if (requests.length === 0) return;
    setSavingPrices(String(plan.plan_id));
    setError(null);
    try {
      await Promise.all(requests);
      setSuccess(`Pricing updated for ${plan.name} (${requests.length} ${requests.length === 1 ? "price" : "prices"}).`);
      fetchPlans();
    } catch (err: any) {
      setError(err?.data?.message || "Failed to save pricing.");
    } finally {
      setSavingPrices(null);
    }
  };

  // ── Feature Toggle ────────────────────────────────────────────────────────
  const handleFeatureToggle = async (featureKey: string, enabled: boolean) => {
    if (!featurePlan) return;
    setSavingFeature(true);
    try {
      await getHttpClient(
        `${HTTPService.PLANS_APPADMIN}/${featurePlan.plan_id}/features`,
        "POST",
        { feature_key: featureKey, enabled },
        null,
        null
      );
      setFeaturePlan((prev) => {
        if (!prev) return prev;
        const existing = prev.features.find((f) => f.feature_key === featureKey);
        if (existing) {
          return { ...prev, features: prev.features.map((f) => (f.feature_key === featureKey ? { ...f, enabled } : f)) };
        }
        return { ...prev, features: [...prev.features, { feature_key: featureKey, enabled }] };
      });
      setSuccess(`Feature "${featureLabel(featureKey)}" ${enabled ? "enabled" : "disabled"}.`);
      fetchPlans();
    } catch (err: any) {
      setError(err?.data?.message || "Failed to update feature.");
    } finally {
      setSavingFeature(false);
    }
  };

  const openEdit = (plan?: Plan) => {
    if (plan) {
      setEditPlan(plan);
      setIsNewPlan(false);
    } else {
      setEditPlan(emptyPlan);
      setIsNewPlan(true);
    }
    setEditDialog(true);
  };

  const openFeatures = (plan: Plan) => {
    setFeaturePlan(plan);
    setFeatureDialog(true);
  };

  const isFeatureEnabled = (plan: Plan | null, key: string) =>
    plan?.features.find((f) => f.feature_key === key)?.enabled ?? false;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3} mb={4}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Box>
          <Typography fontSize="18px" fontWeight={700}>Plan Management</Typography>
          <Typography fontSize="13px" color="text.secondary">
            Configure plans, regional pricing, and feature flags.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => openEdit()}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
        >
          Add Plan
        </Button>
      </Box>

      {plans.length === 0 && (
        <Card variant="outlined" sx={{ p: 6, textAlign: "center", borderRadius: "16px" }}>
          <Typography color="text.secondary" fontSize="14px">No plans found. Create your first plan.</Typography>
        </Card>
      )}

      <Stack spacing={2.5}>
        {plans.map((plan) => {
          const dirty = isPlanDirty(plan);
          const isSaving = savingPrices === String(plan.plan_id);
          const cells = priceMatrix[String(plan.plan_id)] || {};
          const enabledFeatures = plan.features?.filter((f) => f.enabled) || [];
          return (
            <Card
              key={plan.plan_id}
              variant="outlined"
              sx={{
                borderRadius: "16px",
                borderColor: "divider",
                borderLeft: `4px solid ${plan.theme_color || "#6366F1"}`,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={2}
                flexWrap="wrap"
                sx={{ p: 3, pb: 2 }}
              >
                <Box>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography fontWeight={700} fontSize="17px">{plan.name}</Typography>
                    {plan.popular && <Chip icon={<Star sx={{ fontSize: 14 }} />} label="Popular" size="small" color="warning" sx={{ fontWeight: 600, height: 22 }} />}
                    {plan.recommended && <Chip icon={<Recommend sx={{ fontSize: 14 }} />} label="Recommended" size="small" color="primary" sx={{ fontWeight: 600, height: 22 }} />}
                    {plan.badge && <Chip label={plan.badge} size="small" sx={{ fontWeight: 600, height: 22 }} />}
                    {plan.checkout_type === "CONTACT" && <Chip label="Contact Sales" size="small" variant="outlined" sx={{ fontWeight: 600, height: 22 }} />}
                    {plan.visibility === "HIDDEN" && <Chip icon={<VisibilityOff sx={{ fontSize: 14 }} />} label="Hidden" size="small" sx={{ height: 22 }} />}
                    {!plan.is_active && <Chip label="Inactive" size="small" color="error" sx={{ height: 22 }} />}
                  </Box>
                  <Typography fontSize="13px" color="text.secondary" mt={0.5}>{plan.description || "—"}</Typography>
                </Box>
                <Box display="flex" gap={0.5}>
                  <Tooltip title="Edit plan details">
                    <IconButton size="small" onClick={() => openEdit(plan)}><Edit fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Manage features">
                    <IconButton size="small" onClick={() => openFeatures(plan)}><Tune fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete plan">
                    <IconButton size="small" color="error" onClick={() => handleDeletePlan(plan.plan_id)}><Delete fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Pricing matrix */}
              <Box sx={{ px: 3, py: 2.5, bgcolor: "#FAFBFC", borderTop: "1px solid", borderColor: "divider" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
                  <Typography fontSize="12px" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing="0.04em">
                    Regional Pricing · per store
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    disabled={!dirty || isSaving}
                    onClick={() => handleSavePrices(plan)}
                    startIcon={isSaving ? <CircularProgress size={13} color="inherit" /> : <Save sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", py: 0.5 }}
                  >
                    {dirty ? "Save changes" : "Saved"}
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr 1fr", sm: "minmax(150px, 1.4fr) 1fr 1fr" },
                    columnGap: 2,
                    rowGap: 1.25,
                    alignItems: "center",
                  }}
                >
                  {/* Column headers */}
                  <Typography fontSize="11px" fontWeight={700} color="text.disabled" textTransform="uppercase">Region</Typography>
                  <Typography fontSize="11px" fontWeight={700} color="text.disabled" textTransform="uppercase">Monthly</Typography>
                  <Typography fontSize="11px" fontWeight={700} color="text.disabled" textTransform="uppercase">Yearly</Typography>

                  {COUNTRY_OPTIONS.map((c) => (
                    <React.Fragment key={c.code}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <span style={{ fontSize: 18, lineHeight: 1 }}>{c.flag}</span>
                        <Box>
                          <Typography fontSize="13px" fontWeight={600} lineHeight={1.2}>{c.label}</Typography>
                          <Typography fontSize="11px" color="text.secondary" lineHeight={1.2}>{c.currency}</Typography>
                        </Box>
                      </Box>
                      {CYCLES.map((cy) => {
                        const key = cellKey(c.code, cy);
                        const value = cells[key] ?? "";
                        const changed = value !== originalPrice(plan, c.code, cy);
                        return (
                          <TextField
                            key={cy}
                            type="number"
                            size="small"
                            placeholder="—"
                            value={value}
                            onChange={(e) => setCell(plan.plan_id, c.code, cy, e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start" sx={{ "& p": { fontSize: "13px", fontWeight: 600 } }}>
                                  {CURRENCY_SYMBOL[c.currency] || ""}
                                </InputAdornment>
                              ),
                            }}
                            inputProps={{ min: 0, style: { padding: "7px 4px", fontSize: "13px", fontWeight: 600 } }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                bgcolor: "#fff",
                                borderRadius: "8px",
                                ...(changed && {
                                  "& fieldset": { borderColor: "primary.main", borderWidth: 1.5 },
                                }),
                              },
                            }}
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>
                {dirty && (
                  <Typography fontSize="11px" color="primary.main" mt={1.25} fontWeight={500}>
                    You have unsaved price changes. Empty fields are left unchanged.
                  </Typography>
                )}
              </Box>

              {/* Features */}
              <Box sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography fontSize="12px" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing="0.04em">
                    Features · {enabledFeatures.length}/{FEATURE_KEYS.length}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Tune sx={{ fontSize: 15 }} />}
                    onClick={() => openFeatures(plan)}
                    sx={{ textTransform: "none", fontSize: "12px", fontWeight: 600 }}
                  >
                    Manage
                  </Button>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={0.75}>
                  {enabledFeatures.length > 0 ? (
                    enabledFeatures.map((f) => (
                      <Chip key={f.feature_key} label={featureLabel(f.feature_key)} size="small" sx={{ fontSize: "11px", fontWeight: 500, bgcolor: "#EEF2FF", color: "#4338CA" }} />
                    ))
                  ) : (
                    <Typography fontSize="12px" color="text.secondary">No features enabled.</Typography>
                  )}
                </Box>
              </Box>
            </Card>
          );
        })}
      </Stack>

      {/* ── Plan Edit/Create Dialog ─────────────────────────────────────────── */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{isNewPlan ? "Create New Plan" : "Edit Plan"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            <TextField
              label="Plan Name"
              fullWidth
              size="small"
              value={editPlan.name || ""}
              onChange={(e) => setEditPlan((p) => ({ ...p, name: e.target.value }))}
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={editPlan.description || ""}
              onChange={(e) => setEditPlan((p) => ({ ...p, description: e.target.value }))}
            />
            <Box display="flex" gap={2}>
              <TextField
                label="Sort Order"
                type="number"
                size="small"
                sx={{ width: 120 }}
                value={editPlan.sort_order ?? 0}
                onChange={(e) => setEditPlan((p) => ({ ...p, sort_order: Number(e.target.value) }))}
              />
              <TextField
                label="Badge Text"
                size="small"
                placeholder="e.g. Most Popular"
                value={editPlan.badge || ""}
                onChange={(e) => setEditPlan((p) => ({ ...p, badge: e.target.value }))}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Theme Color"
                size="small"
                type="color"
                sx={{ width: 90 }}
                value={editPlan.theme_color || "#6366F1"}
                onChange={(e) => setEditPlan((p) => ({ ...p, theme_color: e.target.value }))}
              />
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap">
              <FormControlLabel
                control={<Switch checked={!!editPlan.is_active} onChange={(e) => setEditPlan((p) => ({ ...p, is_active: e.target.checked }))} size="small" />}
                label={<Typography fontSize="13px">Active</Typography>}
              />
              <FormControlLabel
                control={<Switch checked={editPlan.visibility === "VISIBLE"} onChange={(e) => setEditPlan((p) => ({ ...p, visibility: e.target.checked ? "VISIBLE" : "HIDDEN" }))} size="small" />}
                label={<Typography fontSize="13px" display="flex" alignItems="center" gap={0.5}>{editPlan.visibility === "VISIBLE" ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />} Visible</Typography>}
              />
              <FormControlLabel
                control={<Switch checked={!!editPlan.popular} onChange={(e) => setEditPlan((p) => ({ ...p, popular: e.target.checked }))} size="small" />}
                label={<Typography fontSize="13px">Popular</Typography>}
              />
              <FormControlLabel
                control={<Switch checked={!!editPlan.recommended} onChange={(e) => setEditPlan((p) => ({ ...p, recommended: e.target.checked }))} size="small" />}
                label={<Typography fontSize="13px">Recommended</Typography>}
              />
              <FormControlLabel
                control={<Switch checked={!!editPlan.is_default} onChange={(e) => setEditPlan((p) => ({ ...p, is_default: e.target.checked }))} size="small" />}
                label={<Typography fontSize="13px">Default</Typography>}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setEditDialog(false)} sx={{ textTransform: "none", fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={handleSavePlan}
            variant="contained"
            disabled={saving || !editPlan.name}
            startIcon={saving ? <CircularProgress size={14} /> : <Save />}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
          >
            {isNewPlan ? "Create Plan" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Features Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={featureDialog} onClose={() => setFeatureDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Feature Flags — {featurePlan?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={0.5} mt={1}>
            {FEATURE_KEYS.map((key) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    size="small"
                    checked={isFeatureEnabled(featurePlan, key)}
                    onChange={(e) => handleFeatureToggle(key, e.target.checked)}
                    disabled={savingFeature}
                  />
                }
                label={
                  <Typography fontSize="13px" fontWeight={500}>
                    {featureLabel(key)}
                  </Typography>
                }
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => { setFeatureDialog(false); fetchPlans(); }} variant="contained" sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default PlanCustomizer;
